import chatApi from 'api/chat';
import {
  Alert,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PersonIcon from '@mui/icons-material/Person';
import { DashboardLayout } from 'layouts/dashboard/DashboardLayout';
import { Scrollbar } from 'components/Scrollbar';
import { FormEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import type SimpleBarCore from 'simplebar-core';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'error';
  content?: string;
  html?: string;
  error?: string;
}

const SUGGESTED_PROMPTS = [
  'Show water level readings for the past 7 days',
  'List all active devices in my organization',
  'Which devices reported the highest readings this month?',
];

const MIN_QUERY_LENGTH = 5;

const aiMessageStyles = {
  '& .ai-response': {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    minWidth: 0,
    maxWidth: '100%',
  },
  '& .ai-message': {
    m: 0,
    fontSize: '0.875rem',
    lineHeight: 1.6,
    color: 'text.primary',
    overflowWrap: 'anywhere',
  },
  '& .ai-table-wrapper': {
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: 360,
    maxWidth: '100%',
  },
  '& .ai-table': {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.8125rem',
  },
  '& .ai-table th': {
    px: 1.5,
    py: 1,
    textAlign: 'left',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    bgcolor: 'neutral.50',
    borderBottom: '1px solid',
    borderColor: 'divider',
  },
  '& .ai-table td': {
    px: 1.5,
    py: 1,
    whiteSpace: 'nowrap',
    borderBottom: '1px solid',
    borderColor: 'divider',
  },
  '& .ai-table tbody tr:last-of-type td': {
    borderBottom: 'none',
  },
  '& .ai-table tbody tr:hover': {
    bgcolor: 'action.hover',
  },
};

const EmptyState = ({ onSelectPrompt }: { onSelectPrompt: (prompt: string) => void }) => (
  <Stack
    alignItems='center'
    justifyContent='center'
    spacing={3}
    sx={{ height: '100%', minHeight: 280, px: 2, textAlign: 'center' }}
  >
    <Avatar
      sx={{
        width: 56,
        height: 56,
        bgcolor: 'primary.lightest',
        color: 'primary.main',
      }}
    >
      <AutoAwesomeIcon />
    </Avatar>
    <Stack spacing={1} alignItems='center'>
      <Typography variant='h6'>Ask about your sensor data</Typography>
      <Typography variant='body2' color='text.secondary' sx={{ maxWidth: 420 }}>
        Get answers about devices, readings, targets, and groups in plain language. Try one of these
        examples to get started.
      </Typography>
    </Stack>
    <Stack direction='row' flexWrap='wrap' gap={1} justifyContent='center' sx={{ maxWidth: 560 }}>
      {SUGGESTED_PROMPTS.map((prompt) => (
        <Chip
          key={prompt}
          label={prompt}
          onClick={() => onSelectPrompt(prompt)}
          sx={{
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': { bgcolor: 'primary.lightest', borderColor: 'primary.light' },
          }}
        />
      ))}
    </Stack>
  </Stack>
);

const MessageBubble = ({ message }: { message: ChatMessage }) => {
  const isUser = message.role === 'user';
  const isError = message.role === 'error';

  return (
    <Stack
      direction='row'
      spacing={1.5}
      justifyContent={isUser ? 'flex-end' : 'flex-start'}
      alignItems='flex-start'
    >
      {!isUser && (
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: isError ? 'error.lightest' : 'primary.lightest',
            color: isError ? 'error.main' : 'primary.main',
            flexShrink: 0,
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 18 }} />
        </Avatar>
      )}

      <Box sx={{ maxWidth: '85%', minWidth: 0, overflow: 'hidden' }}>
        {isUser && message.content && (
          <Paper
            elevation={0}
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              borderRadius: '16px 16px 4px 16px',
            }}
          >
            <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
              {message.content}
            </Typography>
          </Paper>
        )}

        {isError && message.error && (
          <Alert severity='error' sx={{ borderRadius: 2 }}>
            {message.error}
          </Alert>
        )}

        {message.role === 'assistant' && message.html && (
          <Paper
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '16px 16px 16px 4px',
              overflow: 'hidden',
              bgcolor: 'background.paper',
            }}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant='subtitle2' color='text.secondary'>
                Results
              </Typography>
            </Box>
            <Box
              sx={{ px: 2, py: 1.5, overflow: 'hidden', maxWidth: '100%', ...aiMessageStyles }}
              dangerouslySetInnerHTML={{ __html: message.html }}
            />
          </Paper>
        )}
      </Box>

      {isUser && (
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: 'neutral.200',
            color: 'neutral.700',
            flexShrink: 0,
          }}
        >
          <PersonIcon sx={{ fontSize: 18 }} />
        </Avatar>
      )}
    </Stack>
  );
};

const LoadingBubble = () => (
  <Stack direction='row' spacing={1.5} alignItems='flex-start'>
    <Avatar
      sx={{
        width: 32,
        height: 32,
        bgcolor: 'primary.lightest',
        color: 'primary.main',
      }}
    >
      <AutoAwesomeIcon sx={{ fontSize: 18 }} />
    </Avatar>
    <Paper
      elevation={0}
      sx={{
        px: 2.5,
        py: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '16px 16px 16px 4px',
        bgcolor: 'background.paper',
      }}
    >
      <Stack direction='row' spacing={1.5} alignItems='center'>
        <CircularProgress size={16} thickness={5} />
        <Typography variant='body2' color='text.secondary'>
          Analyzing your question…
        </Typography>
      </Stack>
    </Paper>
  </Stack>
);

const Chat = () => {
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const scrollRef = useRef<SimpleBarCore | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const scrollEl = scrollRef.current?.getScrollElement();
    if (scrollEl) {
      scrollEl.scrollTo({ top: scrollEl.scrollHeight, behavior: 'smooth' });
    }
  }, [messages, submitLoading]);

  const getQueryResult = async (question: string) => {
    const trimmed = question.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) return;

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: trimmed,
    };
    setMessages((prev) => [...prev, userMessage]);
    setQuery('');
    setSubmitLoading(true);

    try {
      const response = await chatApi.generateQuery({
        data: { question: trimmed },
      });

      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: 'assistant',
          html: response.data.html,
        },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: 'error',
          error: 'Try a different question about your devices, readings, or sensor data.',
        },
      ]);
    } finally {
      setSubmitLoading(false);
      inputRef.current?.focus();
    }
  };

  const onSend = (e: FormEvent) => {
    e.preventDefault();
    getQueryResult(query);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!submitLoading && query.trim().length >= MIN_QUERY_LENGTH) {
        getQueryResult(query);
      }
    }
  };

  const canSend = query.trim().length >= MIN_QUERY_LENGTH && !submitLoading;

  return (
    <DashboardLayout>
      <Box
        component='main'
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 64px)',
          py: { xs: 2, md: 4 },
        }}
      >
        <Container
          maxWidth='md'
          sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
        >
          <Stack spacing={0.5} sx={{ mb: 2, flexShrink: 0 }}>
            <Typography variant='h4'>Chat</Typography>
            <Typography variant='body2' color='text.secondary'>
              Ask questions about your devices, readings, and sensor data
            </Typography>
          </Stack>

          <Paper
            elevation={0}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 3,
              overflow: 'hidden',
              bgcolor: 'neutral.50',
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <Scrollbar
                ref={scrollRef}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  '& .simplebar-wrapper': { height: '100%' },
                }}
              >
                {messages.length === 0 && !submitLoading ? (
                  <EmptyState onSelectPrompt={getQueryResult} />
                ) : (
                  <Stack spacing={2.5} sx={{ pb: 1 }}>
                    {messages.map((message) => (
                      <MessageBubble key={message.id} message={message} />
                    ))}
                    {submitLoading && <LoadingBubble />}
                  </Stack>
                )}
              </Scrollbar>
            </Box>

            <Box
              component='form'
              onSubmit={onSend}
              sx={{
                flexShrink: 0,
                p: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <Stack direction='row' spacing={1} alignItems='flex-end'>
                <TextField
                  inputRef={inputRef}
                  fullWidth
                  multiline
                  maxRows={4}
                  placeholder='Ask about your devices or sensor readings…'
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={submitLoading}
                  size='small'
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: 'neutral.50',
                    },
                  }}
                />
                <IconButton
                  type='submit'
                  disabled={!canSend}
                  color='primary'
                  sx={{
                    width: 44,
                    height: 44,
                    flexShrink: 0,
                    bgcolor: canSend ? 'primary.main' : 'neutral.100',
                    color: canSend ? 'primary.contrastText' : 'neutral.400',
                    '&:hover': {
                      bgcolor: canSend ? 'primary.dark' : 'neutral.100',
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'neutral.100',
                      color: 'neutral.400',
                    },
                  }}
                >
                  <SendIcon fontSize='small' />
                </IconButton>
              </Stack>
              <Typography variant='caption' color='text.secondary' sx={{ mt: 1, display: 'block' }}>
                Press Enter to send · Shift+Enter for a new line · Minimum {MIN_QUERY_LENGTH}{' '}
                characters
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>
    </DashboardLayout>
  );
};

export default Chat;
